interface ShipmentPrintData {
  shipmentId: number;
  orderId: number;
  customerName: string;
  note?: string;
  transport_provider?: string;
  items: Array<{
    item_name: string;
    quantity: number;
    note: string;
  }>;
}

export const getShipmentPrintStyles = (): string => `
  * { 
    margin: 0; 
    padding: 0; 
    box-sizing: border-box; 
  }
  
  body { 
    font-family: Arial, sans-serif; 
    padding: 30px; 
    color: #333;
    font-size: 14px;
  }
  
  .print-date {
    text-align: right;
    color: #555;
    font-size: 12px;
    margin-bottom: 20px;
  }
  
  .ids {
    font-size: 15px;
    font-weight: bold;
    margin-bottom: 8px;
  }
  
  .customer {
    font-size: 14px;
    margin-bottom: 20px;
  }
  
  .customer strong {
    color: #555;
  }
  
  .section-title {
    font-size: 12px;
    font-weight: bold;
    color: #666;
    margin-bottom: 6px;
    text-transform: uppercase;
  }
  
  table { 
    width: 100%; 
    border-collapse: collapse; 
    margin-bottom: 25px;
  }
  
  th, td { 
    border: 1px solid #ccc; 
    padding: 8px 10px; 
    text-align: left; 
  }
  
  th { 
    background-color: #f0f0f0; 
    font-weight: bold;
    font-size: 12px;
  }
  
  td {
    font-size: 13px;
  }
  
  .qty-cell {
    text-align: center;
    font-weight: 500;
  }
  
  .footer {
    margin-top: 25px;
    padding-top: 15px;
    border-top: 1px solid #ccc;
  }
  
  .footer-row {
    display: flex;
    gap: 40px;
  }
  
  .footer-item {
    flex: 1;
  }
  
  .footer-label {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 4px;
  }
  
  .footer-line {
    border-bottom: 1px solid #333;
    height: 22px;
    width: 90%;
  }
  
  @page {
    size: A4;
    margin: 15mm;
  }
  
  @media print { 
    @page {
      margin-top: 0;
      margin-bottom: 0;
    }
    
    body { 
      print-color-adjust: exact; 
      -webkit-print-color-adjust: exact;
      padding: 15mm;
      margin: 0;
    }
  }
`;

export const getShipmentPrintHTML = (data: ShipmentPrintData): string => {
  const printDate = new Date().toLocaleString();
  
  const itemRows = data.items
    .map(
      (item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${item.item_name}</td>
        <td class="qty-cell">${item.quantity}</td>
        <td class="qty-cell"></td>
      </tr>
    `
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title></title>
        <style>${getShipmentPrintStyles()}</style>
      </head>
      <body>
        <div class="print-date">${printDate}</div>
        
        <div class="ids">
          Order ID: #${data.orderId} &nbsp;&nbsp;&nbsp;&nbsp; Shipment ID: #${data.shipmentId}
        </div>
        
        <div class="customer">
          <strong>Customer Name:</strong> ${data.customerName}
        </div>
        
        ${data.note ? `
        <div class="section-title">Order Note</div>
        <div style="margin-bottom: 15px; padding: 10px; background-color: #f5f5f5; border-radius: 4px;">
          ${data.note}
        </div>
        ` : ''}
        
        ${data.transport_provider ? `
        <div class="section-title">Transport Provider</div>
        <div style="margin-bottom: 15px; padding: 10px; background-color: #e3f2fd; border-radius: 4px;">
          ${data.transport_provider}
        </div>
        ` : ''}
        
        <div class="section-title">Items</div>
        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Item Name</th>
              <th style="width: 80px; text-align: center;">Qty</th>
              <th style="width: 80px; text-align: center;">Add</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
          </tbody>
        </table>
        
        <div class="footer">
          <div class="footer-row">
            <div class="footer-item">
              <div class="footer-label">Number of CTN:</div>
              <div class="footer-line"></div>
            </div>
            <div class="footer-item">
              <div class="footer-label">Packer Name:</div>
              <div class="footer-line"></div>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;
};

export const printShipment = (data: ShipmentPrintData): void => {
  const printContent = getShipmentPrintHTML(data);
  
  // Create a hidden iframe for printing
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.left = '-9999px';
  
  document.body.appendChild(iframe);
  
  const iframeDoc = iframe.contentWindow?.document;
  if (!iframeDoc) {
    document.body.removeChild(iframe);
    return;
  }
  
  iframeDoc.open();
  iframeDoc.write(printContent);
  iframeDoc.close();
  
  // Wait for content to load then print
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    
    // Remove iframe after print dialog closes
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  };
};
